import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Progress,
  useColorModeValue,
  SimpleGrid,
  Icon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react';
import { Brain, Activity, Zap, BarChart2, GitBranch } from 'lucide-react';

const PerformancePanel = () => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const contentDelivery = [
    {
      domain: "Clinical Guidelines",
      accuracy: 98.2,
      relevance: 95.4,
      engagement: 87.6,
      trend: "+2.3%"
    },
    {
      domain: "Wellness Programs",
      accuracy: 96.8,
      relevance: 94.2,
      engagement: 92.1,
      trend: "+4.1%"
    },
    {
      domain: "Practice Efficiency",
      accuracy: 97.5,
      relevance: 96.8,
      engagement: 89.4,
      trend: "+1.8%"
    }
  ];

  const adaptiveMetrics = [
    {
      title: "Content Adaptation",
      value: "94.8%",
      change: 2.4,
      description: "AI-driven content personalization accuracy"
    },
    {
      title: "Response Time",
      value: "42ms",
      change: -15,
      description: "Average content delivery latency"
    },
    {
      title: "User Satisfaction",
      value: "92%",
      change: 3.2,
      description: "Based on clinician feedback"
    }
  ];

  const modelPerformance = [
    {
      model: "Clinical Context Engine",
      metrics: {
        precision: 0.956,
        recall: 0.934,
        f1Score: 0.945
      },
      improvements: "+2.3%",
      status: "Optimized"
    },
    {
      model: "Domain Adapter",
      metrics: {
        precision: 0.923,
        recall: 0.918,
        f1Score: 0.920
      },
      improvements: "+1.7%",
      status: "Learning"
    },
    {
      model: "Content Synthesizer",
      metrics: {
        precision: 0.967,
        recall: 0.942,
        f1Score: 0.954
      },
      improvements: "+3.1%",
      status: "Optimized"
    }
  ];

  return (
    <VStack spacing={6} align="stretch">
      {/* Adaptive Performance */}
      <Box>
        <Text fontSize="lg" fontWeight="medium" mb={4}>Adaptive System Performance</Text>
        <SimpleGrid columns={3} spacing={4}>
          {adaptiveMetrics.map((metric, idx) => (
            <Box
              key={idx}
              bg={bgColor}
              p={4}
              borderRadius="lg"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <Stat>
                <StatLabel>{metric.title}</StatLabel>
                <StatNumber color="methodist.blue">{metric.value}</StatNumber>
                <StatHelpText>
                  {metric.change > 0 && '+'}{metric.change}% from baseline
                </StatHelpText>
                <Text fontSize="sm" color="gray.500">{metric.description}</Text>
              </Stat>
            </Box>
          ))}
        </SimpleGrid>
      </Box>

      {/* Content Delivery Performance */}
      <Box>
        <Text fontSize="lg" fontWeight="medium" mb={4}>Content Delivery Performance</Text>
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Domain</Th>
              <Th>Accuracy</Th>
              <Th>Relevance</Th>
              <Th>Engagement</Th>
              <Th>Trend</Th>
            </Tr>
          </Thead>
          <Tbody>
            {contentDelivery.map((domain, idx) => (
              <Tr key={idx}>
                <Td>
                  <Text fontWeight="medium">{domain.domain}</Text>
                </Td>
                <Td>
                  <VStack align="start" spacing={1}>
                    <Text fontSize="sm" fontWeight="medium">{domain.accuracy}%</Text>
                    <Progress 
                      value={domain.accuracy} 
                      size="sm" 
                      width="100px" 
                      colorScheme="green" 
                    />
                  </VStack>
                </Td>
                <Td>
                  <VStack align="start" spacing={1}>
                    <Text fontSize="sm" fontWeight="medium">{domain.relevance}%</Text>
                    <Progress 
                      value={domain.relevance} 
                      size="sm" 
                      width="100px" 
                      colorScheme="blue" 
                    />
                  </VStack>
                </Td>
                <Td>
                  <VStack align="start" spacing={1}>
                    <Text fontSize="sm" fontWeight="medium">{domain.engagement}%</Text>
                    <Progress 
                      value={domain.engagement} 
                      size="sm" 
                      width="100px" 
                      colorScheme="purple" 
                    />
                  </VStack>
                </Td>
                <Td>
                  <Badge colorScheme="green">{domain.trend}</Badge>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Model Performance */}
      <Box>
        <Text fontSize="lg" fontWeight="medium" mb={4}>Model Performance</Text>
        <SimpleGrid columns={3} spacing={4}>
          {modelPerformance.map((item, idx) => (
            <Box
              key={idx}
              bg={bgColor}
              p={4}
              borderRadius="lg"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <VStack align="start" spacing={3}>
                <HStack justify="space-between" width="100%">
                  <Text fontWeight="medium">{item.model}</Text>
                  <Badge 
                    colorScheme={item.status === 'Optimized' ? 'green' : 'blue'}
                  >
                    {item.status}
                  </Badge>
                </HStack>
                <SimpleGrid columns={2} spacing={2} width="100%">
                  <Text fontSize="sm">Precision:</Text>
                  <Text fontSize="sm" fontWeight="medium">{item.metrics.precision.toFixed(3)}</Text>
                  <Text fontSize="sm">Recall:</Text>
                  <Text fontSize="sm" fontWeight="medium">{item.metrics.recall.toFixed(3)}</Text>
                  <Text fontSize="sm">F1 Score:</Text>
                  <Text fontSize="sm" fontWeight="medium">{item.metrics.f1Score.toFixed(3)}</Text>
                </SimpleGrid>
                <Text fontSize="sm" color="green.500">Improvement: {item.improvements}</Text>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      </Box>
    </VStack>
  );
};

export default PerformancePanel;
